import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
} from "@mui/material";
import { ContainerRenderToPDF } from "../../components/ContainerRenderPDF";
import Loading from "../../components/Loading";
import SnackBar from "../../components/SnackBar";
import {
  buscarPdfPrevia,
  aprovarCadastro,
  reprovarCadastro,
  aprovarClientePrevias,
  reprovarClientePrevias,
  solicitarAlteracaoClientePrevias,
  buscarObservacaoTributarioPrevia,
  criarObservacaoPrevia,
} from "../../services/api-planejamento-tributario.service";
import { receberCredito } from "../../services/api-conta-corrente.service";

const ModalPreviewPrevia = ({
  open,
  onClose,
  id,
  handleSnackBar,
  dadosPreviaPDF = [],
  tipoPrevia = "INSS", // "INSS" ou "PISCOFINS" - padrão INSS para compatibilidade
}) => {
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [pdfBase64, setPdfBase64] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfImageSrc, setPdfImageSrc] = useState(null);
  const [renderingPdfImage, setRenderingPdfImage] = useState(false);
  // local copy of preview items returned by API (fallback to prop)
  const [localDadosPrevia, setLocalDadosPrevia] = useState(
    dadosPreviaPDF || []
  );
  const [meta, setMeta] = useState({
    idPrevia: null,
    cnpj: null,
    idCliente: null,
  });
  const [openObsDialog, setOpenObsDialog] = useState(false);
  const [observacaoText, setObservacaoText] = useState("");
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });

  useEffect(() => {
    let mounted = true;
    const fetchPdf = async () => {
      if (!open || !id) return;
      setLoadingPreview(true);
      try {
        const resp = await buscarPdfPrevia(id);
        if (!mounted) return;
        const idClienteFromParam =
          id && typeof id === "object" ? id.idCliente : null;

        // support multiple response shapes (API may return raw base64 string or object)
        let base64FromResp = null;
        try {
          if (typeof resp === "string" && resp.trim() !== "") {
            base64FromResp = resp;
          } else if (resp && typeof resp === "object") {
            base64FromResp =
              resp.pdfBase64 ||
              resp.base64 ||
              resp.pdf ||
              resp.file ||
              resp.data?.pdfBase64 ||
              resp.data?.base64 ||
              resp.data?.pdf ||
              null;
          }
        } catch (e) {
          base64FromResp = null;
        }

        // if API returns structured preview data, use it for HTML rendering
        const possiblePreviewData =
          resp?.dadosPreviaPDF ||
          resp?.dados ||
          resp?.previewItems ||
          resp?.items ||
          resp?.data?.dadosPreviaPDF ||
          null;
        // if it includes a data: prefix, strip it
        if (
          typeof base64FromResp === "string" &&
          base64FromResp.startsWith("data:")
        ) {
          const parts = base64FromResp.split(",");
          base64FromResp = parts.length > 1 ? parts[1] : parts[0];
        }
        // debug when API didn't return base64 where expected
        if (
          !base64FromResp &&
          !(possiblePreviewData && possiblePreviewData.length > 0)
        ) {
          // No debug needed - API didn't return expected data
        }

        // if API returns structured preview data, use it for HTML rendering
        if (
          possiblePreviewData &&
          Array.isArray(possiblePreviewData) &&
          possiblePreviewData.length > 0
        ) {
          setLocalDadosPrevia(possiblePreviewData);
        }

        if (base64FromResp) {
          setPdfBase64(base64FromResp);
        }

        // try multiple shapes to get idPrevia
        const idPreviaFromResp =
          resp?.idPrevia ||
          resp?.idPreviaInss ||
          resp?.data?.idPrevia ||
          resp?.data?.idPreviaInss ||
          (id && typeof id === "object"
            ? id.idPrevia || id.idPreviaInss
            : null) ||
          null;

        setMeta({
          idPrevia: idPreviaFromResp,
          cnpj:
            resp?.cnpj || (id && typeof id === "object" ? id.cnpj : id) || null,
          idCliente: resp?.idCliente || idClienteFromParam || null,
        });

        // if neither structured data nor base64 present, show error
        if (
          !base64FromResp &&
          (!possiblePreviewData || possiblePreviewData.length === 0)
        ) {
          handleSnackBar?.({
            type: "error",
            msg: resp?.mensagem || "PDF não encontrado.",
          });
          setPdfBase64(null);
        }
      } catch (err) {
        if (mounted) {
          handleSnackBar?.({
            type: "error",
            msg: "Erro ao buscar PDF da prévia.",
          });
          setPdfBase64(null);
          setLocalDadosPrevia([]);
          setMeta({
            idPrevia: null,
            cnpj: id && typeof id === "object" ? id.cnpj : id,
            idCliente: id && typeof id === "object" ? id.idCliente : null,
          });
        }
      } finally {
        if (mounted) setLoadingPreview(false);
      }
    };
    fetchPdf();
    return () => {
      mounted = false;
      // cleanup object URL when component unmounts
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
      }
    };
  }, [open, id, handleSnackBar]);

  // create blob URL whenever pdfBase64 changes
  useEffect(() => {
    if (!pdfBase64) {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
      }
      setPdfImageSrc(null);
      return;
    }

    try {
      const byteCharacters = atob(pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      // revoke previous
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      setPdfUrl(url);
      // If we have a PDF base64, attempt to render first page to image (async)
      try {
        // run in background
        renderPdfBase64ToImage(pdfBase64)
          .then((img) => {
            if (img) setPdfImageSrc(img);
          })
          .catch(() => {});
      } catch (e) {}
    } catch (err) {
      handleSnackBar?.({
        type: "error",
        msg: "Erro ao processar prévia em PDF.",
      });
      setPdfUrl(null);
    }

    return () => {
      // cleanup will be handled in next effect run or unmount
    };
  }, [pdfBase64]);

  // Render PDF base64 to image (first page) using pdf.js loaded from CDN dynamically
  const renderPdfBase64ToImage = async (base64) => {
    try {
      if (!base64) return null;
      // already an image? skip
      const s = String(base64 || "");
      const isPng = s.startsWith("iVBOR");
      const isJpeg = s.startsWith("/9j/");
      const isGif = s.startsWith("R0lG");
      const isDataImage = s.startsWith("data:image");
      if (isPng || isJpeg || isGif || isDataImage) return null;

      setRenderingPdfImage(true);
      // ensure pdfjs is loaded
      if (!window.pdfjsLib) {
        await new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.src =
            "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js";
          script.onload = () => resolve();
          script.onerror = () =>
            reject(new Error("Failed to load pdfjs script"));
          document.head.appendChild(script);
        });
      }
      const pdfjsLib = window.pdfjsLib;
      if (!pdfjsLib) return null;
      // set workerSrc if available
      try {
        pdfjsLib.GlobalWorkerOptions.workerSrc =
          "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js";
      } catch (e) {}

      const binary = atob(base64);
      const len = binary.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);

      const loadingTask = pdfjsLib.getDocument({ data: bytes });
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 1.5 });
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const renderContext = { canvasContext: context, viewport };
      await page.render(renderContext).promise;
      const dataUrl = canvas.toDataURL("image/png");
      setRenderingPdfImage(false);
      return dataUrl;
    } catch (e) {
      setRenderingPdfImage(false);
      return null;
    }
  };

  const handleAprovar = async () => {
    const cnpj = meta.cnpj || (id && typeof id === "object" ? id.cnpj : null);
    const idCliente =
      meta.idCliente || (id && typeof id === "object" ? id.idCliente : null);
    if (!cnpj && !idCliente) {
      handleSnackBar?.({
        type: "error",
        msg: "CNPJ/ID do cliente não disponível para aprovar.",
      });
      return;
    }
    try {
      setLoadingPreview(true);
      if (cnpj) {
        if (!meta.idPrevia) {
          handleSnackBar?.({
            type: "error",
            msg: "idPrevia não encontrado para aprovação.",
          });
          setLoadingPreview(false);
          return;
        }
        await aprovarClientePrevias(cnpj, { idPrevia: meta.idPrevia });
        
        // Calcular valor total da prévia
        const totalGeralBase = (localDadosPrevia || dadosPreviaPDF || []).reduce(
          (acc, item) => acc + (item.valor || 0),
          0
        );
        
        // Criar crédito na conta corrente após aprovar prévia (só INSS).
        // PIS/COFINS lança o crédito apenas na aprovação do projeto, pelo backend (TXM-558).
        if (totalGeralBase > 0 && cnpj && tipoPrevia === "INSS") {
          try {
            const cnpjLimpo = String(cnpj).replace(/\D/g, '');
            const tipoCredito = "INSS";
            const externalEventId = `previa-${tipoPrevia.toLowerCase()}-${meta.idPrevia}-aprovacao-${Date.now()}`;

            await receberCredito({
              cnpj: cnpjLimpo,
              projeto_codigo: null, // Será atualizado quando o projeto for gerado
              tipo_credito: tipoCredito,
              valor: totalGeralBase,
              external_event_id: externalEventId,
              sistema_origem: "app-planejamento-tributario",
            });

            console.log(`✅ Crédito criado na conta corrente após aprovação da prévia de ${tipoPrevia}`);
          } catch (creditoError) {
            console.error("⚠️ Erro ao criar crédito na conta corrente:", creditoError);
            // Não falhar a aprovação se houver erro ao criar crédito
            // O crédito pode ser criado manualmente depois
          }
        }
      } else {
        await aprovarCadastro({ idCliente });
      }
      handleSnackBar?.({
        type: "success",
        msg: "Cadastro aprovado com sucesso.",
      });
      onClose();
    } catch (err) {
      handleSnackBar?.({ type: "error", msg: "Erro ao aprovar cadastro." });
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleReprovar = async () => {
    const cnpj = meta.cnpj || (id && typeof id === "object" ? id.cnpj : null);
    const idCliente =
      meta.idCliente || (id && typeof id === "object" ? id.idCliente : null);
    if (!cnpj && !idCliente) {
      handleSnackBar?.({
        type: "error",
        msg: "CNPJ/ID do cliente não disponível para reprovar.",
      });
      return;
    }
    try {
      setLoadingPreview(true);
      if (cnpj) {
        if (!meta.idPrevia) {
          handleSnackBar?.({
            type: "error",
            msg: "idPrevia não encontrado para reprovação.",
          });
          setLoadingPreview(false);
          return;
        }
        await reprovarClientePrevias(cnpj, { idPrevia: meta.idPrevia });
      } else {
        await reprovarCadastro({ idCliente });
      }
      handleSnackBar?.({
        type: "success",
        msg: "Cadastro reprovado com sucesso.",
      });
      onClose();
    } catch (err) {
      handleSnackBar?.({ type: "error", msg: "Erro ao reprovar cadastro." });
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleOpenObservacao = () => setOpenObsDialog(true);
  const handleCloseObservacao = () => {
    setOpenObsDialog(false);
    setObservacaoText("");
  };

  const handleSubmitObservacao = async () => {
    const cnpj = meta.cnpj || (id && typeof id === "object" ? id.cnpj : null);
    const idCliente =
      meta.idCliente || (id && typeof id === "object" ? id.idCliente : null);
    if (!cnpj && !idCliente) {
      handleSnackBar?.({
        type: "error",
        msg: "CNPJ/ID do cliente não disponível para observação.",
      });
      return;
    }
    try {
      setLoadingPreview(true);
      // Prefer endpoint that links observation to a previa if we have idPrevia + cnpj
      if (meta.idPrevia && meta.cnpj) {
        const payload = {
          observacao: observacaoText,
          idPrevia: meta.idPrevia,
          cnpj: meta.cnpj,
        };
        const resp = await criarObservacaoPrevia(payload);
        handleSnackBar?.({
          type: "success",
          msg:
            resp?.mensagem ||
            resp?.message ||
            "Observação registrada com sucesso.",
        });
      } else if (cnpj) {
        const resp = await solicitarAlteracaoClientePrevias(cnpj, {
          comentario: observacaoText,
        });
        handleSnackBar?.({
          type: "success",
          msg:
            resp?.mensagem ||
            resp?.message ||
            "Observação registrada com sucesso.",
        });
      } else {
        await reprovarCadastro({ idCliente, observacao: observacaoText });
        handleSnackBar?.({
          type: "success",
          msg: "Observação registrada com sucesso.",
        });
      }
      handleCloseObservacao();
      onClose();
    } catch (err) {
      handleSnackBar?.({ type: "error", msg: "Erro ao enviar observação." });
    } finally {
      setLoadingPreview(false);
    }
  };

  const titleId =
    meta.idPrevia ||
    meta.cnpj ||
    (id && typeof id === "object" ? id.cnpj : id) ||
    "";

  const formatValor = (valor) =>
    valor?.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  const totalGeralBase = (localDadosPrevia || dadosPreviaPDF || []).reduce(
    (acc, item) => acc + (item.valor || 0),
    0
  );

  return (
    <>
      <Dialog
        open={!!open}
        onClose={onClose}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            height: "90vh",
            width: "90vw",
            maxWidth: "none",
            m: 0,
            p: 0,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle>Prévia - {titleId}</DialogTitle>
        <DialogContent dividers style={{ backgroundColor: "#fff" }}>
          {loadingPreview ? (
            <div style={{ padding: 24 }}>
              <Loading loading={true} />
            </div>
          ) : pdfBase64 ? (
            // If API returns base64 for an image (png/jpg/gif), render it full-size.
            // If API returned a PDF base64, do NOT render an inline viewer — only offer open/download actions.
            (() => {
              const s = String(pdfBase64 || "");
              const isPng = s.startsWith("iVBOR"); // PNG base64
              const isJpeg = s.startsWith("/9j/"); // JPEG base64
              const isGif = s.startsWith("R0lG"); // GIF base64
              const isDataImage = s.startsWith("data:image");
              const isImage = isPng || isJpeg || isGif || isDataImage;
              if (isImage) {
                const src = isDataImage
                  ? s
                  : `data:${
                      isPng ? "image/png" : isJpeg ? "image/jpeg" : "image/gif"
                    };base64,${s}`;
                return (
                  <div
                    style={{
                      boxSizing: "border-box",
                      padding: 8,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={src}
                      alt="Prévia"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </div>
                );
              }
              // Not an image (likely PDF): try to show rendered image if available, otherwise show only action buttons
              if (renderingPdfImage || pdfImageSrc) {
                return (
                  <div
                    style={{
                      boxSizing: "border-box",
                      padding: 8,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {renderingPdfImage ? (
                      <Loading loading={true} />
                    ) : (
                      <img
                        src={pdfImageSrc}
                        alt="Prévia PDF"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                        }}
                      />
                    )}
                  </div>
                );
              }
              return (
                <div
                  style={{
                    height: "100%",
                    boxSizing: "border-box",
                    padding: 8,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      justifyContent: "center",
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => window.open(pdfUrl, "_blank")}
                    >
                      Abrir em nova aba
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => {
                        const a = document.createElement("a");
                        a.href = pdfUrl;
                        a.download = `previa-${titleId}.pdf`;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                      }}
                    >
                      Baixar PDF
                    </Button>
                  </div>
                </div>
              );
            })()
          ) : (
            <ContainerRenderToPDF
              filename={`previa-${titleId}.pdf`}
              orientation="portrait"
              sx={{
                padding: "20px",
                backgroundColor: "#fff",
                height: "100%",
                boxSizing: "border-box",
              }}
            >
              {(localDadosPrevia || dadosPreviaPDF || []).length > 0 ? (
                <>
                  <Typography variant="h6" gutterBottom>
                    Resumo das Categorias
                  </Typography>

                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#002d72" }}>
                        <TableCell
                          sx={{
                            border: "1px solid #0b0b0b",
                            color: "#fff",
                            fontWeight: "bold",
                          }}
                        >
                          Item
                        </TableCell>
                        <TableCell
                          sx={{
                            border: "1px solid #0b0b0b",
                            color: "#fff",
                            fontWeight: "bold",
                          }}
                        >
                          Verba Trabalhista (Rubricas)
                        </TableCell>
                        <TableCell
                          sx={{
                            border: "1px solid #0b0b0b",
                            color: "#fff",
                            fontWeight: "bold",
                          }}
                        >
                          Fundamentação
                        </TableCell>
                        <TableCell
                          sx={{
                            border: "1px solid #0b0b0b",
                            color: "#fff",
                            fontWeight: "bold",
                          }}
                          align="right"
                        >
                          Valor
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(localDadosPrevia || dadosPreviaPDF).map((item, idx) => (
                        <TableRow
                          key={idx}
                          sx={{
                            backgroundColor:
                              item.medidaJudicialCategoria === "SUGESTAO"
                                ? "#dceefb"
                                : item.medidaJudicialCategoria ===
                                  "DESNECESSIDADE"
                                ? "#e3e3e3"
                                : "#fff",
                          }}
                        >
                          <TableCell sx={{ border: "1px solid #0b0b0b" }}>
                            {idx + 1}
                          </TableCell>
                          <TableCell sx={{ border: "1px solid #0b0b0b" }}>
                            {item.descricaoCategoria}
                          </TableCell>
                          <TableCell sx={{ border: "1px solid #0b0b0b" }}>
                            {item.fundamentacaoCategoria}
                          </TableCell>
                          <TableCell
                            sx={{
                              border: "1px solid #0b0b0b",
                              textAlign: "right",
                            }}
                          >
                            {formatValor(item.valor)}
                          </TableCell>
                        </TableRow>
                      ))}

                      <TableRow>
                        <TableCell sx={{ border: "none" }} />
                        <TableCell sx={{ border: "none" }} />
                        <TableCell
                          sx={{
                            border: "1px solid #0b0b0b",
                            fontWeight: "bold",
                          }}
                        >
                          Total
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            border: "1px solid #0b0b0b",
                            fontWeight: "bold",
                          }}
                        >
                          {formatValor(totalGeralBase)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </>
              ) : (
                <div style={{ padding: 16 }}>
                  <Typography>Prévia não disponível.</Typography>
                </div>
              )}
            </ContainerRenderToPDF>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Fechar</Button>
          <Button onClick={handleReprovar}>Recusar</Button>
          <Button onClick={handleAprovar}>Aprovar</Button>
          <Button onClick={handleOpenObservacao}>Adicionar Observação</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openObsDialog}
        onClose={handleCloseObservacao}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Adicionar Observação</DialogTitle>
        <DialogContent>
          <TextField
            value={observacaoText}
            onChange={(e) => setObservacaoText(e.target.value)}
            label="Observação"
            multiline
            rows={4}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseObservacao}>Cancelar</Button>
          <Button onClick={handleSubmitObservacao} variant="contained">
            Enviar Observação
          </Button>
        </DialogActions>
      </Dialog>

      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={setObjSnackBar} />
    </>
  );
};

export default ModalPreviewPrevia;
